import { Box, Stack } from '@mui/joy';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import partFinderNoFamilies from 'assets/partFinderNoFamilies.svg';
import { useGetPartFinderFamilies } from 'companySettings/hooks';
import { AgGridTable, Button, PageTitle } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider, SelectionCountProvider } from 'core/states';
import { Color, Size } from 'theme/enums';

import {
  EditPartFinderFamilyButton,
  PublishPartFinderFamiliesButton,
  RemovePartFinderFamiliesButton,
  UnpublishPartFinderFamiliesButton,
} from './actions';
import { getColumnDefs } from './columns';
import { UpsertPartFinderFamilyButton } from './UpsertPartFinderFamily';
import { PartFinderEmptyState } from '../PartFinderEmptyState';

const TABLE_MIN_HEIGHT = 320;

// Columns can be made as narrow as the value list's and product type's.
const DEFAULT_COL_DEF = { minWidth: 40 };

export const PartFinderFamilies = () => {
  const { t } = useTranslation();

  const { records, isLoading, loadingPercentage, currentPage } = useGetPartFinderFamilies();

  const columnDefs = useMemo(() => getColumnDefs(), []);

  const actionButtons = useCallback(
    () => [<UpsertPartFinderFamilyButton key="addPartFinderFamilyButton" />],
    []
  );

  // Every row update clears the selection, so the selected rows are always the latest ones.
  const selectionActionButtons = useMemo(
    () => [
      <EditPartFinderFamilyButton key="editPartFinderFamilyButton" />,
      <PublishPartFinderFamiliesButton key="publishPartFinderFamiliesButton" />,
      <UnpublishPartFinderFamiliesButton key="unpublishPartFinderFamiliesButton" />,
      <RemovePartFinderFamiliesButton key="removePartFinderFamiliesButton" />,
    ],
    []
  );

  const emptyStateElement = useMemo(
    () => (
      <PartFinderEmptyState
        image={partFinderNoFamilies}
        message={t('messagePartFinderNoFamilies')}
        action={
          <UpsertPartFinderFamilyButton
            buttonElement={
              <Button
                text={t('buttonLabelAddPartFinderFamily')}
                color={Color.SUCCESS}
                size={Size.MD}
              />
            }
          />
        }
      />
    ),
    [t]
  );

  return (
    <Stack sx={{ height: '100%' }}>
      <PageTitle title={t('titlePartFinderFamilies')} />

      {/* CompanySettingsLayout gives its child a definite height, so the grid takes what is left
          and its footer stays in view; the floor keeps it usable on short viewports. */}
      <Box sx={{ flexGrow: 1, minHeight: TABLE_MIN_HEIGHT, overflow: 'hidden' }}>
        <SelectionCountProvider>
          <AgGridClientTableProvider
            tableId="partFinderFamilies"
            entityType="Family"
            columnDefs={columnDefs}
            data={records}
            isDataLoading={isLoading}
            loadingPercentage={loadingPercentage}
            dataCurrentPage={currentPage}
            layoutType={TableLayoutType.PART_FINDER_FAMILY}
            hasLayoutActions
            headerCheckbox
            defaultColDef={DEFAULT_COL_DEF}
          >
            <AgGridTable
              actionButtons={actionButtons}
              selectionActionButtons={selectionActionButtons}
              emptyStateElement={emptyStateElement}
              hideTopActionsWhenEmpty
            />
          </AgGridClientTableProvider>
        </SelectionCountProvider>
      </Box>
    </Stack>
  );
};
